import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";

// auth0-spa-js 1.x clears its token cache on logout but never revokes the
// refresh token, which stays usable at Auth0 until it expires. Revoke it first.
// The tenant has "Refresh Token Revocation Deletes Grant" on, so this signs the
// user out of HQ in every browser (an accepted trade-off, 2026-10-27).

const CACHE_PREFIX = "@@auth0spajs@@::";
const REVOKE_TIMEOUT_MS = 3000;

const cachedRefreshTokens = (clientId) => {
  const tokens = new Set();
  try {
    const storage = window.localStorage;
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key?.startsWith(`${CACHE_PREFIX}${clientId}::`)) continue;
      try {
        const token = JSON.parse(storage.getItem(key))?.body?.refresh_token;
        if (token) tokens.add(token);
      } catch (error) {
        // Not a token entry; skip it.
      }
    }
  } catch (error) {
    // Storage is blocked; there is nothing to revoke.
  }
  return [...tokens];
};

// Form-encoded keeps this a simple CORS request (no preflight), and keepalive
// lets it finish after logout() navigates away.
const revokeRefreshToken = async (domain, clientId, token) => {
  const response = await fetch(`https://${domain}/oauth/revoke`, {
    method: "POST",
    body: new URLSearchParams({ client_id: clientId, token }),
    keepalive: true,
  });
  if (!response.ok) {
    throw new Error(`Refresh token revoke failed (${response.status})`);
  }
};

const waitAtMost = (promise, ms) => {
  let timer;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = setTimeout(resolve, ms);
    }),
  ]).finally(() => clearTimeout(timer));
};

export const useSignOut = () => {
  const { logout } = useAuth0();

  return useCallback(async () => {
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const revocations = cachedRefreshTokens(clientId).map((token) =>
      revokeRefreshToken(domain, clientId, token).catch((error) =>
        Sentry.captureException(error),
      ),
    );
    await waitAtMost(Promise.all(revocations), REVOKE_TIMEOUT_MS);
    logout({ returnTo: window.location.origin });
  }, [logout]);
};
